import React from 'react'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'

import Checkbox from '@material-ui/core/Checkbox'
// @src imports
import { days, months, years } from 'src/helpers/dates'
import { getDetailedCountries, Maybe, OptionalYearDateInput } from 'src/graphql'
import { Button, Icon, Tooltip } from 'src/chrome'
import {
  useEffect,
  useEffectOnChange,
  useQueryFutures,
  useState,
} from 'src/hooks'

import styles from './addressForm.module.scss'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { formatCountriesAndRegions } from 'src/helpers/formatCountriesAndRegions'
import { Spacer, Text } from '@sendoutcards/quantum-design-ui'

const threeUp: React.CSSProperties = {
  // width: '33.333%',
  marginRight: 20,
  paddingRight: 15,
}
const cancelButton: React.CSSProperties = {
  marginRight: 20,
}

export type FormContact = {
  id?: string
  firstName: string | null
  lastName: string | null
  address1: string | null
  address2: string | null
  company: string | null
  city: string | null
  state: string | null
  country: string | null
  postalCode: string | null
  birthday?: OptionalYearDateInput | null
  anniversary?: OptionalYearDateInput | null
}

export type NonNullableAttributes<T> = {
  [K in keyof T]: NonNullable<T[K]>
}

const isEmptyDate = (date: Partial<OptionalYearDateInput>) =>
  !date.month && !date.day && !date.year

const isValidDate = (
  date: Partial<OptionalYearDateInput>,
): date is OptionalYearDateInput =>
  Boolean((date.month && date.day && date.year) || (date.month && date.day))

const getValidDate = (date: Partial<OptionalYearDateInput>) =>
  isValidDate(date) ? date : undefined

interface Props {
  shouldShowDates: boolean | undefined
  style?: React.CSSProperties
  contact?: FormContact
  isSaving?: boolean
  shouldHideActions?: boolean
  checkboxText?: string
  secondaryCheckboxText?: string
  isSecondaryCheckboxChecked?: boolean
  confirmButtonText?: string
  disableCountryReason?: string
  errorMessages?: Maybe<string>[]
  onSubmit?: (
    contact: NonNullableAttributes<FormContact>,
    isCheckboxChecked: boolean,
    isSecondaryCheckboxChecked: boolean,
  ) => void
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onCountryChange?: (country: string) => void
  onStateChange?: (state: string) => void
  toggleAddressBlinker?: () => void
  close?: () => void
  isTitleVisible: boolean
}

const AddressForm: React.FC<Props> = props => {
  const {
    contact,
    style,
    shouldShowDates,
    isTitleVisible,
    onCountryChange,
    onStateChange,
    disableCountryReason,
    isSaving,
    shouldHideActions,
    checkboxText,
    secondaryCheckboxText,
    confirmButtonText,
    errorMessages,
    onSubmit,
    onChange,
    toggleAddressBlinker,
    close,
  } = props

  const [firstName, setFirstName] = useState(contact?.firstName ?? '')
  const [lastName, setLastName] = useState(contact?.lastName ?? '')
  const [address1, setAddress1] = useState(contact?.address1 ?? '')
  const [address2, setAddress2] = useState(contact?.address2 ?? '')
  const [company, setCompany] = useState(contact?.company ?? '')
  const [city, setCity] = useState(contact?.city ?? '')
  const [state, setState] = useState(contact?.state ?? '')
  const [country, setCountry] = useState(contact?.country ?? 'United States')
  const [postalCode, setPostalCode] = useState(contact?.postalCode ?? '')
  const [birthday, setBirthday] = useState<Partial<OptionalYearDateInput>>({})
  const [anniversary, setAnniversary] = useState<
    Partial<OptionalYearDateInput>
  >({})

  const [detailedCountries] = useQueryFutures(getDetailedCountries())
  const { countries, regions, isRegionsUnavailable } = detailedCountries.value
    ? formatCountriesAndRegions(detailedCountries.value, country)
    : { countries: [], regions: undefined, isRegionsUnavailable: false }

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false)

  const [isSecondaryCheckboxChecked, setIsSecondaryCheckboxChecked] = useState(
    props.isSecondaryCheckboxChecked ?? false,
  )
  const isInvalidRegion = !regions?.includes(state)
  const isBirthdayInvalid = !isEmptyDate(birthday) && !isValidDate(birthday)
  const isAnniversaryInvalid =
    !isEmptyDate(anniversary) && !isValidDate(anniversary)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (isBirthdayInvalid || isAnniversaryInvalid) return

    onSubmit?.(
      {
        id: contact?.id,
        firstName,
        lastName,
        address1,
        address2,
        company,
        city,
        state,
        country,
        postalCode,
        birthday: shouldShowDates ? getValidDate(birthday) : undefined,
        anniversary: shouldShowDates ? getValidDate(anniversary) : undefined,
      },
      isCheckboxChecked,
      isSecondaryCheckboxChecked,
    )

    toggleAddressBlinker?.()
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onChange?.(e as React.ChangeEvent<HTMLInputElement>)
    return true
  }

  useEffectOnChange(() => {
    onCountryChange?.(country)
    if (isRegionsUnavailable) {
      setState(' ')
    }
  }, [country])

  useEffectOnChange(() => {
    onStateChange?.(state)
  }, [state])

  useEffect(() => {
    if (!contact) return

    setFirstName(contact.firstName ?? '')
    setLastName(contact.lastName ?? '')
    setAddress1(contact.address1 ?? '')
    setAddress2(contact.address2 ?? '')
    setCompany(contact.company ?? '')
    setCity(contact.city ?? '')
    setState(contact.state ?? '')
    setCountry(contact.country ?? '')
    setPostalCode(contact.postalCode ?? '')
    setBirthday(contact.birthday ?? {})
    setAnniversary(contact.anniversary ?? {})
  }, [contact])

  return (
    <div
      className={`${styles.addressForm} ${contact ? styles.editContact : ''}`}
      style={style}
    >
      <form className={styles.formContainer} onSubmit={handleSubmit}>
        {isTitleVisible && (
          <div className={`${styles.flex} ${styles.drawerHeader}`}>
            <Icon icon={'ADDUSER'} />
            <Spacer space="x_5" orientation="horizontal" />
            <Text type="body">
              {contact ? 'Edit address' : 'Add a new address'}
            </Text>
          </div>
        )}
        <div className={`${styles.contactField} ${styles.twoX}`}>
          <TextField
            id={'first_name_field'}
            autoFocus={true}
            onChange={e => handleChange(e) && setFirstName(e.target.value)}
            value={firstName}
            name="firstName"
            placeholder={'First Name'}
            required={true}
            className={styles.textField}
            style={{ width: '50%', marginRight: '15px' }}
          />
          <TextField
            id={'last_name_field'}
            onChange={e => handleChange(e) && setLastName(e.target.value)}
            value={lastName}
            name="lastName"
            placeholder={'Last Name'}
            required={true}
            className={styles.textField}
            style={{ width: '50%' }}
          />
        </div>
        <div className={styles.contactAddress}>
          <TextField
            id={'address1_field'}
            onChange={e => handleChange(e) && setAddress1(e.target.value)}
            value={address1}
            name="address1"
            placeholder={'Address 1'}
            required={true}
            className={styles.textField}
            fullWidth={true}
          />
        </div>
        <div className={styles.contactAddress}>
          <TextField
            id={'address2_field'}
            onChange={e => handleChange(e) && setAddress2(e.target.value)}
            value={address2}
            name="address2"
            placeholder={'Address 2'}
            required={false}
            className={styles.textField}
            fullWidth={true}
          />
        </div>
        <div className={`${styles.contactField} ${styles.twoX}`}>
          <TextField
            id={'city_field'}
            onChange={e => handleChange(e) && setCity(e.target.value)}
            value={city}
            name="city"
            placeholder={'City'}
            required={true}
            className={styles.textField}
            style={threeUp}
          />
          {!isRegionsUnavailable && (
            <TextField
              id={'state_field'}
              select={true}
              onChange={e => setState(e.target.value)}
              value={state}
              name="state"
              placeholder={state}
              required={true}
              className={styles.textField}
              style={threeUp}
              error={!regions?.includes(state)}
              helperText={
                !regions?.includes(state) ? 'Please select a region' : undefined
              }
            >
              {(regions || ['United States']).map((region, index) => (
                <MenuItem
                  id={region.split(' ').join('_').toLowerCase()}
                  key={index}
                  value={region}
                >
                  {region}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            id={'postal_code_field'}
            onChange={e => handleChange(e) && setPostalCode(e.target.value)}
            value={postalCode}
            name="postalCode"
            placeholder={'Postal Code'}
            required={true}
            className={styles.textField}
            style={{ width: '33.333%' }}
          />
        </div>
        <div className={`${styles.contactField} ${styles.twoX}`}>
          <TextField
            id={'country_field'}
            fullWidth={true}
            select={true}
            className={styles.textField}
            value={country}
            name="country"
            onChange={e => setCountry(e.target.value)}
            FormHelperTextProps={{
              style: {
                color: '#9f9f9f',
              },
            }}
            SelectProps={{
              MenuProps: {
                style: {
                  maxHeight: 200,
                },
              },
            }}
            style={{ width: '50%', marginRight: '15px' }}
            disabled={!!disableCountryReason}
          >
            {(countries || ['United States']).map((country, index) => (
              <MenuItem
                id={country.split(' ').join('_').toLowerCase()}
                key={index}
                value={country}
              >
                {country}
              </MenuItem>
            ))}
          </TextField>
          {disableCountryReason && (
            <div style={{ width: 40, display: 'flex', alignItems: 'center' }}>
              <Tooltip
                hoverIcon={'INFO'}
                message={disableCountryReason || 'Disabled right now'}
                position={'top'}
              />
            </div>
          )}
          <TextField
            id={'company_field'}
            onChange={e => handleChange(e) && setCompany(e.target.value)}
            value={company}
            name="company"
            placeholder={'Company'}
            className={styles.textField}
            style={{ width: '50%' }}
          />
        </div>
        {shouldShowDates && (
          <div className={styles.dateFields}>
            <div className={styles.dateHeader}>
              <Text type="body">Birthday</Text>
              {isBirthdayInvalid && (
                <Text
                  type="footnote"
                  color="danger"
                  outset={{ top: '6px', left: 'x_5' }}
                >
                  *invalid date
                </Text>
              )}
            </div>
            <div style={{ display: 'flex' }}>
              <div id={'birthday_month_dropdown'}>
                <TextField
                  label="Month"
                  select={true}
                  fullWidth={true}
                  value={birthday.month}
                  onChange={e =>
                    handleChange(e) &&
                    setBirthday(x => ({
                      ...x,
                      month: parseInt(e.target.value, 10),
                    }))
                  }
                  name="month"
                  style={threeUp}
                  SelectProps={{
                    MenuProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  {months &&
                    months.map((month, index) => {
                      return (
                        <MenuItem
                          id={`birthday_month_${index}`}
                          key={index}
                          value={month === null ? undefined : index}
                        >
                          {month}
                        </MenuItem>
                      )
                    })}
                </TextField>
              </div>
              <div id={'birthday_day_dropdown'}>
                <TextField
                  label="Day"
                  fullWidth={true}
                  select={true}
                  value={birthday.day}
                  onChange={e =>
                    handleChange(e) &&
                    setBirthday(x => ({
                      ...x,
                      day: parseInt(e.target.value, 10),
                    }))
                  }
                  name="day"
                  style={threeUp}
                  SelectProps={{
                    MenuProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  {days &&
                    days(birthday.month || 1).map((day, index) => {
                      return (
                        <MenuItem
                          id={`birthday_day_${index}`}
                          key={index}
                          value={day ?? undefined}
                        >
                          {day}
                        </MenuItem>
                      )
                    })}
                </TextField>
              </div>
              <TextField
                label="Year"
                fullWidth={true}
                select={true}
                value={birthday.year}
                onChange={e =>
                  handleChange(e) &&
                  setBirthday(x => ({
                    ...x,
                    year: parseInt(e.target.value, 10),
                  }))
                }
                name="year"
                style={{ width: '75px' }}
                SelectProps={{
                  MenuProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                {years &&
                  years.map((year, index) => {
                    return (
                      <MenuItem key={index} value={year ?? undefined}>
                        {year}
                      </MenuItem>
                    )
                  })}
              </TextField>
            </div>
          </div>
        )}
        {shouldShowDates && (
          <div className={styles.dateFields}>
            <div className={styles.dateHeader}>
              <Text type="body">Anniversary</Text>
              {isAnniversaryInvalid && (
                <Text
                  type="footnote"
                  color="danger"
                  outset={{ top: '6px', left: 'x_5' }}
                >
                  *invalid date
                </Text>
              )}
            </div>
            <div style={{ display: 'flex' }}>
              <div id={'anniversary_month_dropdown'}>
                <TextField
                  label="Month"
                  fullWidth={true}
                  select={true}
                  value={anniversary.month}
                  onChange={e =>
                    handleChange(e) &&
                    setAnniversary(x => ({
                      ...x,
                      month: parseInt(e.target.value, 10),
                    }))
                  }
                  name="month"
                  style={threeUp}
                  SelectProps={{
                    MenuProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  {months &&
                    months.map((month, index) => {
                      return (
                        <MenuItem
                          id={`anniversary_month_${index}`}
                          key={index}
                          value={month === null ? undefined : index}
                        >
                          {month}
                        </MenuItem>
                      )
                    })}
                </TextField>
              </div>
              <div id={'anniversary_day_dropdown'}>
                <TextField
                  label="Day"
                  fullWidth={true}
                  select={true}
                  value={anniversary.day}
                  onChange={e =>
                    handleChange(e) &&
                    setAnniversary(x => ({
                      ...x,
                      day: parseInt(e.target.value, 10),
                    }))
                  }
                  name="day"
                  style={threeUp}
                  SelectProps={{
                    MenuProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  {days &&
                    days(anniversary.month || 1).map((day, index) => {
                      return (
                        <MenuItem
                          id={`anniversary_day_${index}`}
                          key={index}
                          value={day ?? undefined}
                        >
                          {day}
                        </MenuItem>
                      )
                    })}
                </TextField>
              </div>
              <TextField
                label="Year"
                fullWidth={true}
                select={true}
                value={anniversary.year}
                onChange={e =>
                  handleChange(e) &&
                  setAnniversary(x => ({
                    ...x,
                    year: parseInt(e.target.value, 10),
                  }))
                }
                name="year"
                style={{ width: '75px' }}
                SelectProps={{
                  MenuProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                {years &&
                  years.map((year, index) => {
                    return (
                      <MenuItem key={index} value={year ?? undefined}>
                        {year}
                      </MenuItem>
                    )
                  })}
              </TextField>
            </div>
          </div>
        )}
        {checkboxText && (
          <FormControlLabel
            control={
              <Checkbox
                onChange={() => setIsCheckboxChecked(x => !x)}
                checked={isCheckboxChecked}
              />
            }
            label={checkboxText}
          />
        )}
        {secondaryCheckboxText && (
          <FormControlLabel
            control={
              <Checkbox
                onChange={() => setIsSecondaryCheckboxChecked(x => !x)}
                checked={isSecondaryCheckboxChecked}
              />
            }
            label={secondaryCheckboxText}
          />
        )}
        {errorMessages && (
          <>
            {errorMessages.map((m, i) => (
              <div key={i}>{m}</div>
            ))}
          </>
        )}
        {!shouldHideActions && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 25,
            }}
          >
            {close && (
              <Button
                title={'Cancel'}
                type={'button'}
                onClick={close}
                style={cancelButton}
                disabled={isSaving}
              />
            )}
            <Button
              type={'submit'}
              title={
                isSaving
                  ? 'Saving...'
                  : confirmButtonText
                  ? confirmButtonText
                  : !shouldShowDates
                  ? 'Save address'
                  : 'Save Contact'
              }
              buttonColor={isSaving ? undefined : 'pink'}
              innerStyle={{ margin: '0 auto' }}
              id={'save_contact_btn'}
              disabled={isSaving || isInvalidRegion}
            />
          </div>
        )}
      </form>
    </div>
  )
}

export default AddressForm
